import { useState } from 'react';
import type { ConsentTemplate } from '@salonomy/types';
import { Button, Card, Pill, Spinner } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { useConsentTemplates, useUpsertConsentTemplate } from '../../data/consent.js';
import { useAuth } from '../../lib/auth.js';
import { useToast } from '../../lib/toast.js';
import { CONSENT_KIND_LABEL } from '../clients/consent-shared.js';
import { ConsentTemplateModal } from './ConsentTemplateModal.js';

/**
 * Manager-only editor for the salon's CONSENT TEMPLATES (under Settings). Lists every template (we fetch
 * WITHOUT the active filter so deactivated/superseded versions stay visible), and lets a manager:
 *   - CREATE a new form (POST, version 1);
 *   - EDIT a form — changing the wording creates a NEW version server-side (the old row keeps its signed
 *     evidence); the modal makes that explicit;
 *   - DEACTIVATE a live form (PATCH active:false — no version bump), with a typed confirmation.
 *
 * RBAC mirrors LoyaltySettingsPanel: it self-gates on useAuth().isManager and shows a non-manager an
 * honest note rather than a bare 403 as their first feedback. The list read is RLS-scoped (any staff role
 * could read it), but the create/edit/deactivate mutations are manager+ on the API.
 */
export function ConsentTemplatesPanel() {
  const { isManager } = useAuth();
  const { data, isLoading, isError } = useConsentTemplates(undefined);
  const deactivate = useUpsertConsentTemplate();
  const toast = useToast();

  const [editing, setEditing] = useState<{ existing?: ConsentTemplate } | null>(null);
  const [toDeactivate, setToDeactivate] = useState<ConsentTemplate | null>(null);
  const [busy, setBusy] = useState(false);

  // Honest note for a non-manager — never the editor.
  if (!isManager) {
    return (
      <Card>
        <div className="section-title" style={{ marginBottom: 2 }}>
          Consent forms
        </div>
        <div className="muted" style={{ fontSize: 13, padding: '4px 0' }}>
          Only an owner or manager can set up the consent &amp; consultation forms the team captures
          against (e.g. patch-test, allergy, colour consultation).
        </div>
      </Card>
    );
  }

  async function confirmDeactivate() {
    if (!toDeactivate) return;
    setBusy(true);
    try {
      await deactivate.mutateAsync({ id: toDeactivate.id, body: { active: false } });
      toast.success(`${toDeactivate.title} deactivated`);
      setToDeactivate(null);
    } catch {
      toast.error('Could not deactivate this form.');
    } finally {
      setBusy(false);
    }
  }

  const templates = data ?? [];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="section-title">Consent forms</div>
        <Button size="sm" onClick={() => setEditing({})}>
          New form
        </Button>
      </div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
        Consultation &amp; consent forms the team captures against (typed-name attestation). Editing
        the wording creates a new version — signed records keep their frozen copy.
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading consent forms…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load your consent forms. Please try again.
        </div>
      ) : templates.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          No consent forms yet. Create one to start capturing consultations, patch tests and
          waivers.
        </div>
      ) : (
        <div className="settings-list">
          {templates.map((t) => (
            <div key={t.id} className="settings-row">
              <div>
                <div style={{ fontWeight: 600 }}>
                  {t.title}{' '}
                  <span className="mono muted" style={{ fontSize: 12 }}>
                    v{t.version}
                  </span>
                  {!t.active ? (
                    <Pill tone="danger" className="mono">
                      {t.supersededBy ? 'Superseded' : 'Inactive'}
                    </Pill>
                  ) : null}
                </div>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {CONSENT_KIND_LABEL[t.kind]}
                  {t.validityDays != null ? ` · valid ${t.validityDays} days` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Button size="sm" variant="ghost" onClick={() => setEditing({ existing: t })}>
                  Edit
                </Button>
                {t.active ? (
                  <Button size="sm" variant="ghost" onClick={() => setToDeactivate(t)}>
                    Deactivate
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <ConsentTemplateModal existing={editing.existing} onClose={() => setEditing(null)} />
      ) : null}

      <ConfirmModal
        open={Boolean(toDeactivate)}
        title="Deactivate consent form"
        confirmLabel="Deactivate"
        confirmPhrase={toDeactivate?.title}
        busy={busy}
        onConfirm={confirmDeactivate}
        onClose={() => setToDeactivate(null)}
      >
        <p style={{ margin: 0 }}>
          Deactivating <strong>{toDeactivate?.title}</strong> hides it from the capture flow — staff
          can no longer sign clients against it. Records already signed against it are unaffected
          and keep their frozen wording.
        </p>
      </ConfirmModal>
    </Card>
  );
}
